import { Controller } from "@hotwired/stimulus"

const TOKEN_PATTERN = /\[([^\]]+)\]\((\/(?:things|extras)\/\d+)\)|!([a-zA-Z0-9_]+)|\$([A-Za-z][A-Za-z0-9_-]*)|((?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)/gi
const ASSET_LOOKUP_CACHE = new Map()

export default class extends Controller {
  static targets = ["text"]
  static values = { members: Array }

  connect() {
    this.renderRequestId = 0
    this.renderingInternal = false
    this.lastSourceText = null
    this.boundDecryptReady = this.handleDecryptReady.bind(this)
    this.element.addEventListener("decrypt:ready", this.boundDecryptReady)
    this.startObserver()

    this.scheduleRender(this.currentSourceText())
  }

  disconnect() {
    this.element.removeEventListener("decrypt:ready", this.boundDecryptReady)
    this.stopObserver()
  }

  handleDecryptReady(event) {
    this.scheduleRender(event.detail?.text || this.currentSourceText())
  }

  async renderText(text) {
    if (!this.hasTextTarget) return

    const requestId = ++this.renderRequestId
    const fragment = document.createDocumentFragment()
    const members = (this.membersValue || []).map(member => ({
      ...member,
      aliasLower: String(member.alias || "").toLowerCase()
    }))
    const membersByAlias = new Map(members.map(member => [member.aliasLower, member]))
    TOKEN_PATTERN.lastIndex = 0
    const matches = Array.from(text.matchAll(TOKEN_PATTERN))
    const assetTokens = [...new Set(matches.map(match => String(match[4] || "").toUpperCase()).filter(Boolean))]
    const assetMatches = new Map()

    await Promise.all(assetTokens.map(async (token) => {
      assetMatches.set(token, await this.lookupAssetMatch(token))
    }))

    let cursor = 0

    matches.forEach((match) => {
      if (match.index > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, match.index)))
      }

      if (match[1] && match[2]) {
        const link = this.buildLink({
          href: match[2],
          text: match[1],
          className: "chewy-channel-bubble-asset-link",
          color: "#89ff9d"
        })
        fragment.appendChild(link)
      } else if (match[3]) {
        const mention = membersByAlias.get(String(match[3]).toLowerCase())
        if (mention) {
          const link = this.buildLink({
            href: mention.profilePath,
            text: `!✉ ${mention.alias}`,
            className: "chewy-channel-bubble-mention-link",
            color: "#ffcf78",
            turbo: false
          })
          fragment.appendChild(link)
        } else {
          fragment.appendChild(document.createTextNode(match[0]))
        }
      } else if (match[4]) {
        const asset = assetMatches.get(String(match[4]).toUpperCase())
        if (asset?.path) {
          const link = this.buildLink({
            href: asset.path,
            text: match[0],
            className: "chewy-channel-bubble-asset-link",
            color: "#89ff9d"
          })
          fragment.appendChild(link)
        } else {
          fragment.appendChild(document.createTextNode(match[0]))
        }
      } else if (match[5]) {
        const { href, label, trailing } = this.normalizeExternalLink(match[5])
        if (href.present) {
          const link = this.buildLink({
            href: href.value,
            text: label,
            className: "chewy-channel-bubble-asset-link",
            color: "#8fe7ff",
            external: true
          })
          fragment.appendChild(link)
          if (trailing) {
            fragment.appendChild(document.createTextNode(trailing))
          }
        } else {
          fragment.appendChild(document.createTextNode(match[0]))
        }
      }

      cursor = match.index + match[0].length
    })

    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)))
    }

    if (requestId !== this.renderRequestId || !this.hasTextTarget) return
    this.renderingInternal = true
    this.textTarget.replaceChildren(fragment)
    this.lastSourceText = text
    requestAnimationFrame(() => {
      this.renderingInternal = false
    })
  }

  startObserver() {
    if (!this.hasTextTarget) return

    this.observer = new MutationObserver(() => {
      if (this.renderingInternal) return
      this.scheduleRender(this.currentSourceText())
    })

    this.observer.observe(this.textTarget, {
      childList: true,
      characterData: true,
      subtree: true
    })
  }

  stopObserver() {
    if (!this.observer) return
    this.observer.disconnect()
    this.observer = null
  }

  scheduleRender(text) {
    const nextText = String(text || "")
    if (!nextText.trim()) return
    if (nextText === this.lastSourceText) return
    this.renderText(nextText)
  }

  currentSourceText() {
    if (!this.hasTextTarget) return ""
    return this.textTarget.textContent || ""
  }

  async lookupAssetMatch(query) {
    const normalized = String(query || "").trim().toUpperCase()
    if (!normalized) return null

    if (!ASSET_LOOKUP_CACHE.has(normalized)) {
      ASSET_LOOKUP_CACHE.set(normalized, this.fetchAssetMatch(normalized))
    }

    return ASSET_LOOKUP_CACHE.get(normalized)
  }

  async fetchAssetMatch(query) {
    try {
      const response = await fetch(`/things/linkify_lookup?query=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })

      if (!response.ok) return null

      const payload = await response.json()
      const candidates = []
      const queryLower = query.toLowerCase()

      ;(payload.things || []).forEach((thing) => {
        candidates.push({
          path: `/things/${thing.id}`,
          score: this.assetScore({
            queryLower,
            ticker: thing.ticker,
            title: thing.title
          })
        })
      })

      ;(payload.extras || []).forEach((extra) => {
        candidates.push({
          path: `/extras/${extra.id}`,
          score: this.assetScore({
            queryLower,
            title: extra.title,
            parentTitle: extra.thingTitle
          })
        })
      })

      return candidates
        .filter((candidate) => Number.isFinite(candidate.score))
        .sort((left, right) => left.score - right.score)[0] || null
    } catch (_error) {
      return null
    }
  }

  assetScore({ queryLower, ticker, title, parentTitle }) {
    const normalizedTicker = String(ticker || "").toLowerCase()
    const normalizedTitle = String(title || "").toLowerCase()
    const normalizedParentTitle = String(parentTitle || "").toLowerCase()

    if (normalizedTicker && normalizedTicker === queryLower) return 0
    if (normalizedTitle && normalizedTitle === queryLower) return 1
    if (normalizedTicker && normalizedTicker.startsWith(queryLower)) return 5
    if (normalizedTitle && normalizedTitle.startsWith(queryLower)) return 10

    const tickerIndex = normalizedTicker.indexOf(queryLower)
    if (tickerIndex >= 0) return 20 + tickerIndex

    const titleIndex = normalizedTitle.indexOf(queryLower)
    if (titleIndex >= 0) return 40 + titleIndex

    const parentIndex = normalizedParentTitle.indexOf(queryLower)
    if (parentIndex >= 0) return 80 + parentIndex

    return Number.POSITIVE_INFINITY
  }

  normalizeExternalLink(rawValue) {
    const cleaned = String(rawValue || "").replace(/[),.!?;:]+$/, "")
    const trailing = String(rawValue || "").slice(cleaned.length)

    if (!cleaned) {
      return { href: { present: false, value: null }, label: rawValue, trailing: "" }
    }

    if (/^https?:\/\//i.test(cleaned)) {
      return { href: { present: true, value: cleaned }, label: cleaned, trailing }
    }

    if (/^www\./i.test(cleaned) || /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/.*)?$/i.test(cleaned)) {
      return { href: { present: true, value: `https://${cleaned}` }, label: cleaned, trailing }
    }

    return { href: { present: false, value: null }, label: rawValue, trailing: "" }
  }

  buildLink({ href, text, className, color, external = false, turbo = true }) {
    const link = document.createElement("a")
    link.href = href
    link.textContent = text
    link.className = className
    link.style.color = color
    link.style.fontWeight = "700"
    link.style.textDecoration = "none"

    if (external) {
      link.target = "_blank"
      link.rel = "noopener noreferrer"
    }

    if (!turbo) {
      link.dataset.turbo = "false"
    }

    return link
  }
}
